'use client';

import { useMemo } from 'react';
import { useAppDispatch } from '@akinon/next/redux/hooks';
import { openMobileMenu } from '@akinon/next/redux/reducers/header';
import { useLocalization, useMediaQuery } from '@akinon/next/hooks';
import { WidgetResultType } from '@akinon/next/types';
import clsx from 'clsx';

import { Icon } from '@theme/components';
import { useTypewriter } from '@theme/hooks';
import { requestMobileSearchFocus } from '@theme/redux/reducers/headerSearch';
import { SearchPlaceholderWordListType } from '@theme/widgets/search-placeholder-word-list/type';

interface SearchPlaceholderBarProps {
  widget: WidgetResultType<SearchPlaceholderWordListType>;
  className?: string;
}

/**
 * Looks like the search field inside the mobile menu, but has no input and no
 * submit button — tapping it opens the mobile menu and focuses the real search
 * field there. The rotating words come from the
 * `search-placeholder-word-list` widget.
 */
export const SearchPlaceholderBar = ({
  widget,
  className
}: SearchPlaceholderBarProps) => {
  const dispatch = useAppDispatch();
  const { t } = useLocalization();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const words = useMemo(() => {
    const widgetWords = (widget?.attributes?.search_placeholder_words ?? [])
      .map((item) => item?.value?.word?.trim())
      .filter(Boolean);

    return widgetWords.length > 0
      ? widgetWords
      : [t('common.search.placeholder')];
  }, [widget, t]);

  const text = useTypewriter(words, { enabled: !prefersReducedMotion });

  return (
    <button
      type="button"
      aria-label={t('common.search.open')}
      data-testid="header-search"
      onClick={() => {
        dispatch(requestMobileSearchFocus());
        dispatch(openMobileMenu());
      }}
      className={clsx(
        'flex items-center bg-transparent border border-[#A5A5A5]/30 rounded-[5rem] pl-1.5 pr-2 h-9 min-w-0 text-left',
        className
      )}
    >
      <Icon name="search" size={20} className="mr-1 shrink-0" />
      <span
        className="text-sm font-normal text-[#A5A5A5] truncate"
        aria-hidden="true"
      >
        {text || ' '}
      </span>
    </button>
  );
};
